'use client';

import NextLink from 'next/link';
import { type ReactNode } from 'react';

import { Role } from '@ariakit/react';
import cx from 'classnames';

import { type TrackEventProps, trackEvent } from '~/ui/components/analytics/trackEvent';
import { useIsFullBleedCTATrigger } from '~/ui/components/fullBleedCta';
import { type IconName } from '~/ui/components/icon';
import { Icon } from '~/ui/components/icon';
import typographyStyles from '~/ui/styles/typography.module.scss';

import styles from './link.module.scss';
import { type Href, getLinkIcon, hrefToString, isInternalLink as isInternalLink } from './utils';

export interface LinkContentProps {
  /**
   * Render an icon with the link
   *
   * When `true`, we'll determine what icon to use automatically depending on whether or not the
   * link is internal or external.
   *
   * @default false
   */
  withIcon?: boolean | IconName;

  /**
   * By default, arrow icons will be placed on the right and all other icons on the left.
   * @default 'auto'
   */
  iconPosition?: 'auto' | 'left' | 'right';
  children?: ReactNode;
  href: Href;
}

export function LinkContent({
  withIcon = false,
  iconPosition = 'auto',
  href,
  children,
}: LinkContentProps) {
  let iconName: IconName | null = null;
  if (withIcon === true) {
    iconName = getLinkIcon(href);
  } else if (withIcon) {
    iconName = withIcon;
  }

  const isArrowIcon =
    iconName === 'expanding-arrow-right' ||
    iconName === 'expanding-arrow-left' ||
    iconName === 'expanding-arrow-diagonal';
  const isRightIcon =
    iconPosition === 'right' ||
    (iconPosition === 'auto' &&
      // exclude left arrow
      (iconName === 'expanding-arrow-right' || iconName === 'expanding-arrow-diagonal'));

  const iconElement = iconName ? (
    <Icon
      name={iconName}
      className={cx(styles.icon, {
        [styles.arrow]: isArrowIcon,
        [styles.right]: isRightIcon,
      })}
    />
  ) : null;

  let content: React.ReactNode;
  if (!iconElement) {
    content = children;
  } else if (typeof children === 'string') {
    const text = children.split(' ');
    if (isRightIcon) {
      const lastWord = text.pop();
      content = (
        <>
          {text.join(' ') + ' '}
          <span className={styles.noWrap}>
            {lastWord}&nbsp;{iconElement}
          </span>
        </>
      );
    } else {
      const firstWord = text.shift();
      content = (
        <>
          <span className={styles.noWrap}>
            {iconElement}&nbsp;{firstWord}
          </span>
          {' ' + text.join(' ')}
        </>
      );
    }
  } else {
    content = (
      <>
        {!isRightIcon && iconElement}
        {children}
        {isRightIcon && iconElement}
      </>
    );
  }

  return content;
}
export interface LinkProps extends LinkContentProps, React.ComponentProps<typeof NextLink> {
  /**
   * Determines whether the link is rendered via `typographyStyles.link` or
   * `typographyStyles.linkLarge`.
   *
   * @default default
   */
  size?: 'default' | 'large';
  /** Call `trackEvent` when the link is clicked */
  event?: TrackEventProps;

  invertHoverStyle?: boolean;
}

export function Link({
  size = 'default',
  withIcon = false,
  iconPosition = 'auto',
  event,
  onClick,
  href,
  className,
  children,
  invertHoverStyle,
  ...props
}: LinkProps) {
  const isInternal = isInternalLink(href);

  const isFullBleed = useIsFullBleedCTATrigger();
  let sizeClass: string;
  switch (size) {
    case 'default':
      sizeClass = typographyStyles.link;
      break;
    case 'large':
      sizeClass = typographyStyles.linkLarge;
      break;
  }

  return (
    <Role.a
      {...props}
      className={cx(styles.link, sizeClass, className, {
        [styles.notIsFullBleed]: !isFullBleed,
        [styles.invertHoverStyle]: invertHoverStyle,
      })}
      onClick={e => {
        if (event) {
          trackEvent(event);
        }
        onClick?.(e);
      }}
      render={
        isInternal ? (
          <NextLink href={href} />
        ) : (
          <a target="_blank" rel="noreferrer" href={hrefToString(href)} />
        )
      }
    >
      <LinkContent iconPosition={iconPosition} withIcon={withIcon} href={href}>
        {children}
      </LinkContent>
    </Role.a>
  );
}
